import { useMutation, useQuery } from '@tanstack/react-query';
import { Form } from 'antd';
import { AnyObject } from 'antd/es/_util/type';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { generalConfigApi, timeKeepingApi } from '../../../apis';
import {
  EmployeeRelatedInformationDtoRolesEnum,
  GetEmployeeTimekeepingHistoryDto,
  GetEmployeeTimekeepingHistoryDtoRequestDayOffEnum,
  TimekeepingReportTypeEnum,
} from '../../../apis/client-axios';
import ButtonWrap from '../../../components/ButtonWrap';
import DatePickerWrap from '../../../components/DatePickerWrap';
import SvgCsvGreen from '../../../components/svg/SvgCsvGreen';
import SvgEdit from '../../../components/svg/SvgEdit';
import TableWrap from '../../../components/TableWrap';
import ToastError from '../../../components/Toast/ToastError';
import ToastSuccess from '../../../components/Toast/ToastSuccess';
import Widget from '../../../components/Widget';
import { MONTH_YEAR_FORMAT_JP, YEAR_MONTH_DATE_FORMAT_JP, YEAR_MONTH_FORMAT } from '../../../constants/common';
import { DAY_START, TIME_KEEPING_API } from '../../../constants/QueryKey';
import type { RootState } from '../../../store';
import { downloadCSV, formatMinutesToHoursAndMinutes } from '../../../utils';
import AttendanceOpTion from '../../Manager/AttendanceConfirm/AttendanceOption';
import './style.scss';
import ModalTimeKeeping from '../../../components/ModalTimekeeping';

const AttendanceConfirm = () => {
  const intl = useIntl();

  const [form] = Form.useForm();
  const month = Form.useWatch('month', form);

  const navigate = useNavigate();

  const employee = useSelector((state: RootState) => state.auth.authUser);

  const [timeKeepingHistory, setTimeKeepingHistory] = useState<GetEmployeeTimekeepingHistoryDto>();

  const columns = [
    {
      title: intl.formatMessage({ id: 'attendance.confirm.column.date' }),
      dataIndex: 'date',
      key: 'date',
      render: (record) => {
        return (
          <p className="attendance-confirm_table-column-data">{dayjs(record).format(YEAR_MONTH_DATE_FORMAT_JP)}</p>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'attendance.confirm.column.attendance' }),
      dataIndex: 'dateType',
      key: 'dateType',
      render: (value, record) => {
        let messageId = value ? `attendance.confirm.dateType.${value}` : '';
        let additionalMessage = '';
        if (record.requestDayOff === GetEmployeeTimekeepingHistoryDtoRequestDayOffEnum.Other) {
          messageId += '_LEAVE';
          const requestDayOffReport = record.reports?.find((rp) => rp.type === TimekeepingReportTypeEnum.RequestDayOff);
          additionalMessage = requestDayOffReport?.option || '';
        }
        return (
          <p className="employee-option_table-column-data">
            {additionalMessage || (intl.messages?.[messageId] ? intl.formatMessage({ id: messageId }) : '')}
          </p>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'attendance.confirm.column.startTime' }),
      dataIndex: 'start',
      key: 'start',
      render: (record) => {
        return <p className="employee-option_table-column-data">{record == null ? '' : record}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'attendance.confirm.column.endTime' }),
      dataIndex: 'end',
      key: 'end',
      render: (record) => {
        return <p className="employee-option_table-column-data">{record == null ? '' : record}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'attendance.confirm.column.breakTime' }),
      dataIndex: 'breakTime',
      key: 'breakTime',
      render: (record) => {
        return (
          <p className="employee-option_table-column-data">
            {record == null ? '' : formatMinutesToHoursAndMinutes(record)}
          </p>
        );
      },
    },
    {
      title: (
        <span
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({ id: 'attendance.confirm.column.regular' }),
          }}
        />
      ),
      dataIndex: 'actualTimeWorking',
      key: 'actualTimeWorking',
      render: (record) => {
        return (
          <p className="employee-option_table-column-data">
            {record == null ? '' : formatMinutesToHoursAndMinutes(record)}
          </p>
        );
      },
    },
    {
      title: (
        <span
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({ id: 'attendance.confirm.column.normalOvertime' }),
          }}
        />
      ),
      dataIndex: 'overtimeDay',
      key: 'overtimeDay',
      render: (record) => {
        return (
          <p className="employee-option_table-column-data">
            {record == null ? '' : formatMinutesToHoursAndMinutes(record)}
          </p>
        );
      },
    },
    {
      title: (
        <span
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({ id: 'attendance.confirm.column.lateNight' }),
          }}
        />
      ),
      dataIndex: 'overtimeNight',
      key: 'overtimeNight',
      render: (record) => {
        return (
          <p className="employee-option_table-column-data">
            {record == null ? '' : formatMinutesToHoursAndMinutes(record)}
          </p>
        );
      },
    },
    {
      title: (
        <span
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({ id: 'attendance.confirm.column.accumulated' }),
          }}
        />
      ),
      dataIndex: 'stackOvertime',
      key: 'stackOvertime',
      render: (record) => {
        return (
          <p className="employee-option_table-column-data">
            {record == null ? '' : formatMinutesToHoursAndMinutes(record)}
          </p>
        );
      },
    },
    {
      title: '',
      dataIndex: 'option',
      key: 'option',
      render: (record, row) => {
        return (
          <ButtonWrap onClick={() => setTimeKeepingHistory(row)} className="employee-option_table-column-button">
            <SvgEdit />
            {intl.formatMessage({ id: 'attendance.confirm.button.edit' })}
          </ButtonWrap>
        );
      },
    },
  ];

  const { data: dayStartData } = useQuery({
    queryKey: [DAY_START],
    queryFn: () => generalConfigApi.generalConfigControllerConfigWorkSchedule(),
  });

  const {
    data: attendanceData,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: [TIME_KEEPING_API, employee?.id, month],
    queryFn: () =>
      timeKeepingApi.timekeepingControllerGetEmployeeTimekeepingHistory(
        employee?.id as string,
        month.format(MONTH_YEAR_FORMAT_JP),
        1,
        undefined,
      ),
    enabled: !!month && !!employee?.id,
  });

  const exportCsv = useMutation({
    mutationFn: async () => {
      return await timeKeepingApi.timekeepingControllerExportTimekeepingToCsv(
        employee?.id as string,
        month.format(MONTH_YEAR_FORMAT_JP),
        1,
        undefined,
      );
    },
    onSuccess: (data: any) => {
      downloadCSV(data?.data?.data, `勤怠管理_${employee?.name}_${dayjs(month).format(YEAR_MONTH_FORMAT)}.csv`);
      ToastSuccess({
        content: intl.formatMessage({ id: 'common.csv.export.success' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'common.csv.export.failed' }),
      });
    },
  });

  useEffect(() => {
    if (employee?.role && employee?.role !== EmployeeRelatedInformationDtoRolesEnum.Employee) {
      navigate(-1);
    }
  }, [navigate, employee?.role]);

  useEffect(() => {
    if (dayStartData?.data?.data?.dayStartOfMonth) {
      const getCurrentPayrollMonth = (dayStartOfMonth: number) => {
        const today = dayjs();
        const currentDay = today.date();

        const startDay = dayStartOfMonth || 2;

        if (currentDay >= startDay) {
          form.setFieldValue('month', dayjs().add(1, 'month'));
        } else {
          form.setFieldValue('month', dayjs());
        }
      };

      const dayStartOfMonth = dayStartData?.data?.data?.dayStartOfMonth;
      getCurrentPayrollMonth(dayStartOfMonth);
    }
    // eslint-disable-next-line
  }, [dayStartData]);

  const handleExport = () => {
    exportCsv.mutate();
  };

  const handleSubmit = () => {
    setTimeKeepingHistory(undefined);
    refetch();
  };

  return (
    <div className="employee-option">
      <Widget
        breadcrumbs={[
          intl.formatMessage(
            { id: 'attendance.confirm.breadcumb' },
            { name: employee?.name || intl.formatMessage({ id: 'attendance.confirm.title' }) },
          ),
        ]}
        content={
          <TableWrap
            isPagination={false}
            loading={isLoading}
            leftTopContent={
              <Form form={form}>
                <Form.Item name={'month'}>
                  <DatePickerWrap picker="month" format={MONTH_YEAR_FORMAT_JP} width={312} allowClear={false} />
                </Form.Item>
              </Form>
            }
            middleContent={
              <>
                <AttendanceOpTion initialFilter={{ month, isUsersScreen: true }} />
              </>
            }
            rightTopContent={
              <div className="employee-option__right-export" onClick={handleExport}>
                <SvgCsvGreen /> <span>{intl.formatMessage({ id: 'common.csv.export.title' })}</span>
              </div>
            }
            tableProps={{
              dataSource: attendanceData?.data.data?.content,
              columns: columns as ColumnsType<AnyObject>,
              className: 'employee-option_table',
              loading: isLoading,
            }}
          />
        }
        title={employee?.name || intl.formatMessage({ id: 'attendance.confirm.title' })}
      />

      {!!timeKeepingHistory && (
        <ModalTimeKeeping
          open={!!timeKeepingHistory}
          timeKeepingHistory={timeKeepingHistory}
          onCancel={() => setTimeKeepingHistory(undefined)}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
};
export default AttendanceConfirm;
